import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';

export const EngineUtils = {
    createCamera(fov = 60, near = 1, far = 5, z = 5) {
        const camera = new THREE.PerspectiveCamera(fov, window.innerWidth / window.innerHeight, near, far);
        camera.position.z = z;
        return camera;
    },

    createScene(color = 0xd3f3f4) {
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(color);
        return scene;
    },

    // Функция для сжатия изображения на лету
    optimizeTextureSource(image, scale = 0.4) {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        canvas.width = image.width * scale;
        canvas.height = image.height * scale;

        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        return canvas; // Возвращаем уменьшенный холст вместо огромной картинки
    },

    createRenderer(container) {
        const isMobile = /Mobi|Android/i.test(navigator.userAgent);
        const renderer = new THREE.WebGLRenderer({
            antialias: !isMobile,
            powerPreference: "high-performance",
            precision: isMobile ? "lowp" : "highp", // Еще сильнее снижаем точность вычислений
        });

        // Для мобилок ставим разрешение ниже 1.0 (например 0.8), это даст эффект "мыла", но спасет FPS
        const pixelRatio = isMobile ? 0.8 : window.devicePixelRatio;
        renderer.setPixelRatio(pixelRatio);
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.shadowMap.enabled = !isMobile;

        container.appendChild(renderer.domElement);
        return renderer;
    },

    disposeObject(obj) {
        if (!obj) return;

        obj.traverse(node => {
            if (node.isMesh) {
                if (node.geometry) node.geometry.dispose();
                if (node.material) {
                    if (Array.isArray(node.material)) {
                        node.material.forEach(m => this.disposeMaterial(m));
                    } else {
                        this.disposeMaterial(node.material);
                    }
                }
            }
        });
    },

    disposeMaterial(mat) {
        // Очистка текстур - САМОЕ ВАЖНОЕ для мобилок
        Object.keys(mat).forEach(prop => {
            if (mat[prop] && mat[prop].isTexture) {
                mat[prop].dispose();
            }
        });
        mat.dispose();
    },

    setupLighting(scene, activeLights = ['p1', 'p2', 'p3']) {
        const hemiLight = new THREE.HemisphereLight(0x87CEEB, 0x443322, 0.6);
        scene.add(hemiLight);

        const createPhysicalBulb = (x, y, z, color = 0xF2E7D9, power = 500) => {
            const light = new THREE.PointLight(color, 1, 6, 2);
            light.power = power;
            light.position.set(x, y, z);
            light.castShadow = true;
            light.shadow.mapSize.set(1024, 1024);
            light.shadow.bias = -0.0001;
            light.shadow.normalBias = 0.05;
            return light;
        };

        const lights = { hemiLight };

        if (activeLights.includes('p1')) {
            lights.p1 = createPhysicalBulb(7.6, 1.8, 0.8, 0xF2E7D9, 250);
            scene.add(lights.p1);
        }
        if (activeLights.includes('p2')) {
            lights.p2 = createPhysicalBulb(1, 1.8, 1.2, 0xF2E7D9, 50);
            scene.add(lights.p2);
        }
        if (activeLights.includes('p3')) {
            lights.p3 = createPhysicalBulb(-1, 1.8, 1.5, 0xF2E7D9, 50);
            scene.add(lights.p3);
        }
        if (activeLights.includes('p4')) {
            lights.p4 = createPhysicalBulb(-1, 1.8, -1, 0xF2E7D9, 50);
            scene.add(lights.p4);
        }

        return lights;
    },

    addLightHelpers(scene, lights) {
        if (lights.p1) scene.add(new THREE.PointLightHelper(lights.p1, 0.3));
        if (lights.p2) scene.add(new THREE.PointLightHelper(lights.p2, 0.3));
        if (lights.p3) scene.add(new THREE.PointLightHelper(lights.p3, 0.3));
        if (lights.p4) scene.add(new THREE.PointLightHelper(lights.p4, 0.3));
    },

    createComposer(renderer, scene, camera) {
        const composer = new EffectComposer(renderer);
        composer.addPass(new RenderPass(scene, camera));
        composer.addPass(new OutputPass());
        return composer;
    },

    handleResize(camera, renderer, composer) {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
        if (composer) composer.setSize(window.innerWidth, window.innerHeight);
    },

    getMouseNDC(event, domElement) {
        const rect = domElement.getBoundingClientRect();
        return {
            x: ((event.clientX - rect.left) / rect.width) * 2 - 1,
            y: -((event.clientY - rect.top) / rect.height) * 2 + 1
        };
    },

    getOrCreateOverlay() {
        let overlay = document.getElementById('scene-loader-overlay');
        if (!overlay) {
            overlay = document.createElement('div');
            overlay.id = 'scene-loader-overlay';
            Object.assign(overlay.style, {
                position: 'fixed', top: '0', left: '0', width: '100%', height: '100%',
                backgroundColor: 'black', display: 'flex', justifyContent: 'center',
                alignItems: 'center', opacity: '0', transition: 'opacity 0.5s ease-in-out',
                pointerEvents: 'none', zIndex: '9999'
            });

            const loader = document.createElement('div');
            Object.assign(loader.style, {
                width: '50px', height: '50px', border: '5px solid #333',
                borderTop: '5px solid white', borderRadius: '50%'
            });

            loader.animate([
                { transform: 'rotate(0deg)' },
                { transform: 'rotate(360deg)' }
            ], { duration: 1000, iterations: Infinity });

            overlay.appendChild(loader);
            document.body.appendChild(overlay);
        }
        return overlay;
    },

    async showOverlay() {
        const overlay = this.getOrCreateOverlay();
        overlay.style.pointerEvents = 'all';
        overlay.style.opacity = '1';
        return new Promise(resolve => setTimeout(resolve, 500));
    },

    hideOverlay() {
        const overlay = document.getElementById('scene-loader-overlay');
        if (overlay) {
            overlay.style.opacity = '0';
            overlay.style.pointerEvents = 'none';
        }
    },
};